import React, { useContext, useEffect } from 'react';
import { createStyles } from 'utils/createStyle';
import { Routes } from 'routes';
import { link } from 'styles/typography';
import { Btn } from 'components/btn/Btn';
import { useLocation, useHistory } from 'react-router-dom';
import { withMessage, combineValidationFns, required } from 'common/modules/form-validation';
import { ShopScreen } from 'features/shop-base/ShopScreen';
import { Formik, FormikHelpers, FormikProps, Form } from 'formik';
import { FormikTextField } from 'components/fields/FormikTextField';
import { getOrganizationByEnrollmentCode } from 'common/api/crm/organization/enrollment';
import { FamilyEnrollmentContext } from './context';
import { useRootSelector } from 'rootStore';
import { selectLoginState } from 'common/features/store/duck/account/duck';
import { RegistrationRequest } from 'common/api/auth/models/RegistrationRequest';
import { GA_WEB_ENROLLMENT_EXPERIMENT_NAME, logParticipantEnrollment } from 'config/analytics';
import { IS_FLIPGIVE } from 'common/env';
import { ExternalLinks } from 'externalLinks';
import { HowRaiseRightWorks } from './HowRaiseRightWorks';
import { ReactComponent as Stars } from 'media/images/stars.svg';
import enrollmentHero from 'media/images/enrollment-hero.png';

const invalidEnrollmentCodeMessage = 'Invalid Enrollment Code';
const invalidEnrollmentCodeWrongCountryMessage =
    "This enrollment code isn't valid on this site. Please check the website and try again.";
const enrollmentCodeValidator = withMessage(invalidEnrollmentCodeMessage)(
    combineValidationFns(required, (value: string) => Boolean(value.match(/^[0-9A-Za-z-]+$/g)))
);

interface EnrollmentForm {
    enrollmentCode: string;
}

const useEnrollmentCodeFromUrl = () => {
    const params = new URLSearchParams(useLocation().search);
    return params.get('enrollCode') || '';
};

const styles = createStyles({
    pageWrapper: [
        'w-full',
        {
            background: 'linear-gradient(180deg, #FFFFFF 35%, #E1F1F7 70%)',
            '@media (min-width: 768px)': { background: 'none' },
        },
    ],
    heroSection: 'w-full',
    heroInner: [
        'mx-auto flex flex-col md:flex-row items-stretch px-6 sm:px-8 pt-10 pb-4 sm:pb-6 sm:pt-16 gap-8 md:gap-12',
        { maxWidth: '1170px' },
    ],
    formColumn: [
        'flex flex-col justify-center items-center md:items-start w-full',
        { '@media (min-width: 768px)': { width: 'calc(50% + 40px)' } },
    ],
    heroColumn: ['hidden md:flex w-1/2 items-center justify-center'],
    heroImage: [
        'w-full object-contain',
        { maxHeight: '432px', marginTop: '-100px', marginLeft: '30px' },
    ],
    starsIcon: [
        {
            display: 'inline',
            width: '27px',
            height: '37px',
            transform: 'rotate(-30deg)',
            verticalAlign: 'text-top',
            position: 'relative',
            top: '-8px',
            marginRight: '8px',
            '@media (min-width: 768px)': {
                display: 'block',
                width: '37px',
                height: '52px',
                marginBottom: '12px',
                position: 'static',
                top: '0',
            },
        },
    ],
    heading: [
        'text-brand-dark font-semibold mb-4',
        {
            fontFamily: 'Open Sans, sans-serif',
            fontSize: '32px',
            lineHeight: '1.1',
            textAlign: 'center',
            '@media (min-width: 768px)': { fontSize: '56px', textAlign: 'left' },
        },
    ],
    subtext: [
        'mb-6 leading-relaxed',
        {
            fontFamily: 'Open Sans, sans-serif',
            fontSize: '16px',
            color: '#424344',
            textAlign: 'center',
            '@media (min-width: 768px)': { fontSize: '20px', textAlign: 'left' },
        },
    ],
    fieldLabel: [
        'font-semibold',
        { color: '#424344', fontFamily: 'Open Sans, sans-serif', fontSize: '16px' },
    ],
    fieldWrapper: ['', { '> label': { marginBottom: '10px' } }],
    legalText: [
        'mt-3',
        {
            fontFamily: 'Open Sans, sans-serif',
            fontSize: '12px',
            color: '#424344',
            textAlign: 'left',
        },
    ],
    ctaButton: [
        'w-full mt-4 mb-2',
        { fontSize: '20px', paddingTop: '14px', paddingBottom: '14px' },
    ],
});

export const JoinProgramLanding = () => {
    const history = useHistory();
    const isUserLoggedIn = useRootSelector(selectLoginState);
    const enrollCode = useEnrollmentCodeFromUrl();
    const { setOrganizationTo, updateUserAccountInfo, isEnrollmentTestVariant } =
        useContext(FamilyEnrollmentContext);

    useEffect(() => {
        if (!isUserLoggedIn) {
            return;
        }

        const currentParams = new URLSearchParams(window.location.search);
        history.push({
            pathname: Routes.ShopAccountSettingsPersonalInformationTab,
            search: `?${currentParams.toString()}`,
        });
    }, [history, isUserLoggedIn]);

    useEffect(() => {
        const variant = isEnrollmentTestVariant ? 'variant' : 'control';
        logParticipantEnrollment(
            2,
            'join_a_program_view',
            { flowVariant: variant },
            {
                experiment_name: GA_WEB_ENROLLMENT_EXPERIMENT_NAME,
                experiment_variant: variant,
            }
        );
    }, [isEnrollmentTestVariant]);

    const submitJoinProgram = async (
        { enrollmentCode }: EnrollmentForm,
        formikHelpers: FormikHelpers<EnrollmentForm>
    ) => {
        const response = await getOrganizationByEnrollmentCode(enrollmentCode);
        if (response.error) {
            formikHelpers.setFieldError('enrollmentCode', invalidEnrollmentCodeMessage);
            return;
        } else if (
            (response.data.countryCode === 'US' && IS_FLIPGIVE) ||
            (response.data.countryCode === 'CA' && !IS_FLIPGIVE)
        ) {
            formikHelpers.setFieldError('enrollmentCode', invalidEnrollmentCodeWrongCountryMessage);
            return;
        }

        const expVariant = isEnrollmentTestVariant ? 'variant' : 'control';
        logParticipantEnrollment(
            1,
            'enrollment_code_submitted',
            {
                orgId: response.data.id,
                flowVariant: expVariant,
            },
            {
                experiment_name: GA_WEB_ENROLLMENT_EXPERIMENT_NAME,
                experiment_variant: expVariant,
            }
        );

        localStorage.setItem('RR_enrollmentCode', enrollmentCode);

        updateUserAccountInfo((currentState: RegistrationRequest) => ({
            ...currentState,
            organizationId: response.data.id,
        }));

        setOrganizationTo(response.data);

        const currentParams = new URLSearchParams(window.location.search);
        history.push({
            pathname: Routes.EnrollFamilyGetStarted,
            search: `?${currentParams.toString()}`,
        });
    };

    if (isUserLoggedIn) {
        return null;
    }

    return (
        <ShopScreen>
            <div className={styles.pageWrapper}>
                <div className={styles.heroSection}>
                    <div className={styles.heroInner}>
                        <div className={styles.formColumn}>
                            <h1 className={styles.heading}>
                                <Stars className={styles.starsIcon} />
                                Join an existing program
                            </h1>
                            <p className={styles.subtext}>
                                To join your organization's fundraising program, enter your
                                enrollment code below. If you don't have a code, ask your
                                fundraising coordinator.
                            </p>
                            <Formik
                                initialValues={{
                                    enrollmentCode: enrollCode || '',
                                }}
                                onSubmit={submitJoinProgram}
                            >
                                {(formikProps: FormikProps<EnrollmentForm>) => (
                                    <Form>
                                        <div>
                                            <FormikTextField
                                                name="enrollmentCode"
                                                autoComplete="off"
                                                className="w-full"
                                                validate={enrollmentCodeValidator}
                                                wrapperStyles={styles.fieldWrapper}
                                                style={{
                                                    padding: '12px 16px',
                                                    borderRadius: '4px',
                                                }}
                                                label={
                                                    <span className={styles.fieldLabel}>
                                                        Enrollment code
                                                    </span>
                                                }
                                                placeholder="Enter enrollment code"
                                            />
                                        </div>
                                        <div>
                                            <Btn
                                                type="button"
                                                onClick={formikProps.submitForm}
                                                className={styles.ctaButton}
                                            >
                                                Continue
                                            </Btn>
                                        </div>
                                        <div className={styles.legalText}>
                                            By clicking <em>Continue</em>, you acknowledge reading
                                            and accepting our{' '}
                                            <a
                                                href={ExternalLinks.PrivacyPolicy}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={link}
                                            >
                                                Privacy Policy
                                            </a>{' '}
                                            and{' '}
                                            <a
                                                href={ExternalLinks.TermsOfUse}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={link}
                                            >
                                                Terms of Use
                                            </a>
                                            .
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                        <div className={styles.heroColumn}>
                            <img
                                src={enrollmentHero}
                                alt="Family fundraising"
                                className={styles.heroImage}
                            />
                        </div>
                    </div>
                </div>
                <HowRaiseRightWorks />
            </div>
        </ShopScreen>
    );
};
